import {
    CssBaseline,
    StyledEngineProvider,
    ThemeProvider,
} from "@mui/material";
import { api } from "api";
import { Route } from "components/Route";
import { useAuthStatus } from "helpers/useAuthStatus";
import { Forbidden } from "pages/Forbidden";
import { RedirectHelpPage } from "pages/RedirectHelp/RedirectHelpPage";
import { HomeSwitch } from "pages/Home";
import { RegisterPage } from "pages/Register";
import { SignInPage } from "pages/SignIn";
import { StudentHomeSwitch } from "pages/StudentHome";
import { GoogleCompleteProfilePage } from "pages/GoogleCompleteProfile/GoogleCompleteProfilePage";
import { useMemo, useEffect } from "react";
import { Switch, Route as ReactRoute, useHistory } from "react-router-dom";
import { errorRoutes, routes } from "routes";
import { defaultTheme, studentTheme } from "theme";
import { useKeycloak } from '@react-keycloak/web';

function AuthHandler(): null {
    const { keycloak, initialized } = useKeycloak();
    const { role, isLoading, setRole } = useAuthStatus();
    const history = useHistory();

    // Keycloak is the source of truth: if its SSO session is gone (e.g. logged out from
    // the game) but a Platform cookie survived, end the Platform session too.
    useEffect(() => {
        if (initialized && !keycloak.authenticated && !isLoading && role !== "unauthenticated") {
            const clear = (): void => setRole("unauthenticated");
            api.signout().then(clear, clear);
        }
    }, [initialized, keycloak.authenticated, isLoading, role, setRole]);

    useEffect(() => {
        if (initialized && keycloak.authenticated && keycloak.tokenParsed && keycloak.token) {
            const email = keycloak.tokenParsed.email;
            const token = keycloak.token;

            if (email) {
                api.signin(
                    { email },
                    { headers: { Authorization: `Bearer ${token}` } },
                )
                    .then((res) => {
                        if (res.status === 200) {
                            return api.authStatus().then((authStatus) => {
                                if (authStatus.status === 200) {
                                    setRole(authStatus.data.role);
                                }
                            });
                        }
                        if (res.status === 403 && res.data?.action === "student_not_allowed") {
                            setRole("unauthenticated");
                            history.replace(errorRoutes._.forbidden.path, { reason: "student" });
                            return;
                        }
                        if (res.status === 404 && res.data?.action === "provision_required") {
                            // Only redirect to teacher provisioning if the user has NO
                            // Keycloak realm roles yet. A student JIT-provisioned on the
                            // student website already has the "student" role — they must
                            // not be re-provisioned as a teacher from this portal.
                            const existingRoles: string[] =
                                (keycloak.tokenParsed as any)?.realm_access?.roles ?? [];
                            const hasAppRole = existingRoles.some((r) =>
                                ["student", "teacher", "admin-teacher", "admin", "parent"].includes(r),
                            );
                            if (!hasAppRole) {
                                history.push(routes._.googleCompleteProfile.path, {
                                    email: res.data.email,
                                    firstName: res.data.firstName,
                                    lastName: res.data.lastName,
                                    keycloakId: res.data.keycloakId,
                                });
                            }
                        }
                    })
                    .catch((error) => {
                        console.error('[AuthHandler] Signin error:', error);
                    });
            }
        }
    }, [initialized, keycloak.authenticated, setRole, history]);

    return null;
}

export const App = (): JSX.Element => {
    const { role } = useAuthStatus();
    const theme = useMemo(
        () => (role === "student" ? studentTheme : defaultTheme),
        [role],
    );

    return (
        <StyledEngineProvider injectFirst>
            <ThemeProvider theme={theme}>
                <CssBaseline />
                <AuthHandler />
                <Switch>
                    <ReactRoute path="/login" exact>
                        <RedirectHelpPage />
                    </ReactRoute>

                    <Route path={routes._.signIn.path} exact>
                        <SignInPage />
                    </Route>

                    <Route path={routes._.register.path} exact>
                        <RegisterPage />
                    </Route>

                    <ReactRoute path={routes._.googleCompleteProfile.path} exact>
                        <GoogleCompleteProfilePage />
                    </ReactRoute>

                    <Route
                        path={[errorRoutes._.error.path, errorRoutes._.forbidden.path]}
                        exact
                    >
                        <Forbidden />
                    </Route>

                    <Route path={routes.path}>
                        {role === "parent" ? (
                            <StudentHomeSwitch />
                        ) : role === "student" ? (
                            <Forbidden reason="student" />
                        ) : (
                            <HomeSwitch />
                        )}
                    </Route>
                </Switch>
            </ThemeProvider>
        </StyledEngineProvider>
    );
};
